import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const BillOfLadingForm = () => {
  const [formData, setFormData] = useState({
    order_id: '',
    bl_no: '',
    bl_type: 'MASTER',
    issue_date: '',
    vessel_name: '',
    origin_port: '',
    origin_country: '',
    departure_date: '',
    eta_port: '',
    arrival_date: '',
    berthing_date: '',
    container_no: '',
    gross_weight_mt: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [orderOptions, setOrderOptions] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();
  const { bl_id } = useParams();

  // Helper function to format date for input
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  // Create date and adjust for timezone offset to prevent date shifting
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};


  useEffect(() => {
    // Check if we're in edit mode
    if (bl_id) {
      setIsEditMode(true);

      // Fetch bill of lading data for edit mode
      const fetchBillOfLadingData = async (id) => {
        setLoading(true);
        try {
          const res = await fetch(`${API_BASE_URL}/api/import/bill-of-lading-single-get/${id}`, {
            credentials: 'include',
          });
          const data = await res.json();
          if (res.ok) {
            const record = data.data;
            setFormData({
              order_id: record.order_id || '',
              bl_no: record.bl_no || '',
              bl_type: record.bl_type || 'MASTER',
              issue_date: formatDateForInput(record.issue_date),
              vessel_name: record.vessel_name || '',
              origin_port: record.origin_port || '',
              origin_country: record.origin_country || '',
              departure_date: formatDateForInput(record.departure_date),
              eta_port: record.eta_port || '',
              arrival_date: formatDateForInput(record.arrival_date),
              berthing_date: formatDateForInput(record.berthing_date),
              container_no: record.container_no || '',
              gross_weight_mt: record.gross_weight_mt || '',
            });
          } else {
            setMessage({ type: 'error', text: data.error || 'Failed to load record' });
          }
        } catch (err) {
          setMessage({ type: 'error', text: 'Server error while loading record' });
          console.error('Fetch bill of lading error:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchBillOfLadingData(bl_id);
    }

    // Fetch orders for dropdown
    const fetchOrders = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-import-order`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (res.ok && Array.isArray(data.data)) {
          setOrderOptions(
            data.data.map(order => ({
              ...order,
              label: `(${order.purchase_contract_no}) (${order.vessel_name})`,
              value: order.order_id
            }))
          );
        }
      } catch (err) {
        console.error('Error fetching import orders:', err);
      }
    };

    fetchOrders();
  }, [bl_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGrossWeightChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, gross_weight_mt: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/import/bill-of-lading-put/${bl_id}`
        : `${API_BASE_URL}/api/import/bill-of-lading-post`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        const successMessage = isEditMode 
          ? 'Bill of Lading updated successfully'
          : 'Bill of Lading saved successfully';
        
        setMessage({ type: 'success', text: successMessage });
        
        if (!isEditMode) {
          // Reset form only for new records
          setFormData({
            order_id: '',
            bl_no: '',
            bl_type: 'MASTER',
            issue_date: '',
            vessel_name: '',
            origin_port: '',
            origin_country: '',
            departure_date: '',
            eta_port: '',
            arrival_date: '',
            berthing_date: '',
            container_no: '',
            gross_weight_mt: '',
          });
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Something went wrong' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server error' });
      console.error('Submit error:', err);
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <div className="flex justify-between items-center border-b pb-3 mb-5">
        <h2 className="text-lg font-medium text-gray-800">
          {isEditMode ? 'Edit Bill of Lading' : 'Bill of Lading Registration'}
        </h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#3899cf] border border-[#10a5e9] px-3 py-1 text-xs rounded font-normal hover:bg-[#10a5e9] hover:text-white hover:border-[#10a5e9] transition"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Purchase Contract*
              {isEditMode && <span className="text-gray-500 text-xs ml-1">(Cannot be changed)</span>}
            </label>
            <SearchableDropdown
              options={orderOptions}
              value={formData.order_id}
              onChange={val => setFormData(prev => ({ ...prev, order_id: val }))}
              placeholder="Select Order"
              displayKey="label"
              valueKey="order_id"
              required
              className={isEditMode ? "opacity-60" : ""}
              disabled={loading || isEditMode} // Disable in edit mode
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">B/L Number *</label>
            <input
              type="text"
              name="bl_no"
              value={formData.bl_no}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">B/L Type *</label>
            <select
              name="bl_type"
              value={formData.bl_type}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
              required
            >
              <option value="MASTER">MASTER</option>
              <option value="HOUSE">HOUSE</option>
              <option value="ENDORSED">ENDORSED</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Issue Date</label>
            <input
              type="date"
              name="issue_date"
              value={formData.issue_date}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Vessel Name</label>
            <input
              type="text"
              name="vessel_name"
              value={formData.vessel_name}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Origin Port</label>
            <input
              type="text"
              name="origin_port"
              value={formData.origin_port}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Origin Country</label>
            <input
              type="text"
              name="origin_country"
              value={formData.origin_country}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Departure Date</label>
            <input
              type="date"
              name="departure_date"
              value={formData.departure_date}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">ETA Port</label>
            <input
              type="text"
              name="eta_port"
              value={formData.eta_port}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Arrival Date</label>
            <input
              type="date"
              name="arrival_date"
              value={formData.arrival_date}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Berthing Date</label>
            <input
              type="date"
              name="berthing_date"
              value={formData.berthing_date}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">No of Containers</label>
            <input
              type="text"
              name="container_no"
              value={formData.container_no}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Gross Weight (MT)</label>
            <input
              type="number"
              step="0.001"
              name="gross_weight_mt"
              value={formData.gross_weight_mt}
              onChange={handleGrossWeightChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
              required
            />
          </div>
        </div>

        <div className="pt-2 flex items-center gap-3">
          <button
            type="submit"
            className="px-4 py-2 text-xs bg-[#3899cf] text-white font-normal rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60"
            style={{ minWidth: '110px' }}
            disabled={loading}
          >
            {loading 
              ? (isEditMode ? 'Updating...' : 'Saving...') 
              : (isEditMode ? 'Update Bill of Lading' : 'Save Bill of Lading')
            }
          </button>
          {message && (
            <div className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default BillOfLadingForm;
